<template>
  <PartnersLayout>
    <!-- 헤더 -->
    <template #header>
      <PartnersHeader
        title="대시보드"
        :subtitle="greetingMessage"
        icon="dashboard"
        iconColor="blue"
      />
    </template>

    <!-- 메인 콘텐츠 -->
    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-7xl mx-auto">
        <!-- 로딩 상태 -->
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <template v-else>
          <!-- 현장 미소속 안내 -->
          <div v-if="!currentProperty" class="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[13px] font-bold text-amber-900">현장 소속이 필요합니다</p>
              </div>
              <router-link
                to="/partners/property/change"
                class="px-3 py-1.5 bg-amber-500 text-white text-[12px] font-bold rounded-lg hover:bg-amber-600 transition-colors flex-shrink-0"
              >
                신청하기
              </router-link>
            </div>
          </div>

          <!-- 상단 배너 슬라이더 -->
          <div v-if="dashboardBanners.length > 0" class="mb-4">
            <div class="relative rounded-2xl overflow-hidden">
              <div class="h-[180px] bg-slate-100">
                <a
                  v-if="dashboardBanners[currentBannerIndex]"
                  :href="dashboardBanners[currentBannerIndex].linkUrl || '#'"
                  @click="handleBannerClick($event, dashboardBanners[currentBannerIndex])"
                  class="block w-full h-full"
                >
                  <img
                    :src="dashboardBanners[currentBannerIndex].imageUrl"
                    class="w-full h-full object-cover"
                    alt="배너"
                  />
                </a>
              </div>
              <!-- 배너 컨트롤 (1/2 왼쪽, <> 오른쪽) -->
              <div class="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span class="px-2 py-1 bg-black/40 text-white text-[11px] font-medium rounded-full">
                  {{ currentBannerIndex + 1 }}/{{ dashboardBanners.length }}
                </span>
                <div class="flex items-center gap-1">
                  <button
                    @click="prevBanner"
                    class="w-7 h-7 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                  <button
                    @click="nextBanner"
                    class="w-7 h-7 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 2단 레이아웃 -->
          <div class="flex flex-col lg:flex-row gap-4">
            <!-- 왼쪽 메인 섹션 -->
            <div class="flex-1 space-y-4">
              <!-- 핵심 지표 -->
              <div class="bg-white border border-slate-200 rounded-2xl p-4">
                <!-- 현장 정보 -->
                <router-link
                  v-if="currentProperty"
                  to="/partners/property/my"
                  class="flex items-center gap-3 pb-4 mb-4 border-b border-slate-100"
                >
                  <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-[11px] font-medium text-slate-400">나의 현장</p>
                    <p class="text-[16px] font-bold text-slate-900 truncate">{{ currentProperty.propertyName }}</p>
                  </div>
                  <svg class="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>

                <!-- 핵심 지표 4개 -->
                <div class="grid grid-cols-2 gap-3">
                  <!-- 신규 DB -->
                  <router-link to="/partners/leads/designated" class="group">
                    <div class="relative flex items-center gap-3 p-3 rounded-xl transition-all group-hover:bg-slate-50"
                         :class="pendingLeadsCount > 0 ? 'bg-blue-50 border border-blue-100' : 'bg-slate-50'">
                      <div class="relative w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                           :class="pendingLeadsCount > 0 ? 'bg-blue-600' : 'bg-slate-200'">
                        <svg class="w-5 h-5" :class="pendingLeadsCount > 0 ? 'text-white' : 'text-slate-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                        </svg>
                        <div v-if="pendingLeadsCount > 0" class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-[11px] font-medium text-slate-400 mb-0.5">신규 DB</p>
                        <p class="text-[22px] font-black leading-none" :class="pendingLeadsCount > 0 ? 'text-blue-600' : 'text-slate-900'">
                          {{ pendingLeadsCount }}
                        </p>
                      </div>
                    </div>
                  </router-link>

                  <!-- 유실 DB -->
                  <router-link to="/partners/leads/open" class="group">
                    <div class="flex items-center gap-3 p-3 rounded-xl transition-all group-hover:bg-slate-50"
                         :class="openLeadsCount > 0 ? 'bg-rose-50 border border-rose-100' : 'bg-slate-50'">
                      <div class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                           :class="openLeadsCount > 0 ? 'bg-rose-500' : 'bg-slate-200'">
                        <svg class="w-5 h-5" :class="openLeadsCount > 0 ? 'text-white' : 'text-slate-500'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-[11px] font-medium text-slate-400 mb-0.5">유실 DB</p>
                        <p class="text-[22px] font-black leading-none" :class="openLeadsCount > 0 ? 'text-rose-500' : 'text-slate-900'">
                          {{ openLeadsCount }}
                        </p>
                      </div>
                    </div>
                  </router-link>

                  <!-- 내 고객 -->
                  <router-link to="/partners/leads/customers" class="group">
                    <div class="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100 transition-all group-hover:bg-emerald-100/50">
                      <div class="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-[11px] font-medium text-slate-400 mb-0.5">내 고객</p>
                        <p class="text-[22px] font-black text-emerald-600 leading-none">{{ myCustomersCount }}</p>
                      </div>
                    </div>
                  </router-link>

                  <!-- 파트너스 포인트 -->
                  <router-link to="/partners/point" class="group">
                    <div class="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 transition-all group-hover:from-blue-100/50 group-hover:to-indigo-100/50">
                      <div class="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span class="text-[13px] font-black text-white">PP</span>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-[11px] font-medium text-slate-400 mb-0.5">파트너스 포인트</p>
                        <p class="text-[18px] font-black text-blue-600 leading-none">{{ formatNumber(pointBalance) }}<span class="text-[12px] font-bold ml-0.5">P</span></p>
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>

              <!-- 파트너스 시작하기 (온보딩) -->
              <div class="bg-white border border-slate-200 rounded-2xl p-4">
                <div class="flex items-center justify-between mb-3">
                  <p class="text-[14px] font-bold text-slate-900">파트너스 시작하기</p>
                  <span class="text-[12px] font-medium text-blue-600">{{ onboardingProgress }}%</span>
                </div>
                <div class="h-2 bg-slate-100 rounded-full mb-4 overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                    :style="{ width: onboardingProgress + '%' }"
                  ></div>
                </div>
                <div class="space-y-2">
                  <div
                    v-for="step in onboardingSteps"
                    :key="step.id"
                    class="flex items-center gap-3 p-2 rounded-lg"
                    :class="step.completed ? 'bg-slate-50' : 'hover:bg-slate-50 cursor-pointer'"
                    @click="!step.completed && step.action && step.action()"
                  >
                    <div :class="[
                      'w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold',
                      step.completed ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                    ]">
                      <svg v-if="step.completed" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <span v-else>{{ step.id }}</span>
                    </div>
                    <span :class="[
                      'text-[13px]',
                      step.completed ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'
                    ]">{{ step.label }}</span>
                  </div>
                </div>
              </div>

              <!-- 진행 중인 광고 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <p class="text-[14px] font-bold text-slate-900">진행 중인 광고</p>
                  <span class="text-[12px] font-medium text-blue-600">{{ activeCampaigns.length + activeJumps.length + activeAds.length }}개</span>
                </div>

                <div v-if="activeCampaigns.length > 0 || activeJumps.length > 0 || activeAds.length > 0" class="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  <!-- 캠페인 -->
                  <router-link
                    v-for="campaign in activeCampaigns.slice(0, 2)"
                    :key="'c-'+campaign.id"
                    to="/partners/ad/my"
                    class="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div class="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded">캠페인</span>
                        <span class="text-[13px] font-medium text-slate-900 truncate">{{ campaign.propertyName }}</span>
                      </div>
                      <p class="text-[11px] text-slate-400 mt-0.5">D-{{ getRemainingDays(campaign.endDate) }}</p>
                    </div>
                  </router-link>

                  <!-- 점프 -->
                  <router-link
                    v-for="jump in activeJumps.slice(0, 2)"
                    :key="'j-'+jump.id"
                    to="/partners/jump/my"
                    class="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div class="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded">점프</span>
                        <span class="text-[13px] font-medium text-slate-900 truncate">{{ jump.propertyName }}</span>
                      </div>
                      <p class="text-[11px] text-slate-400 mt-0.5">~{{ formatShortDate(jump.endDate) }}</p>
                    </div>
                  </router-link>

                  <!-- 기타 광고 -->
                  <component
                    v-for="ad in activeAds.slice(0, 2)"
                    :key="ad.type + '-' + ad.id"
                    :is="'router-link'"
                    :to="getAdRoute(ad.type, 'my')"
                    class="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div class="w-9 h-9 rounded-lg flex items-center justify-center" :class="getAdBgClass(ad.type)">
                      <svg class="w-4 h-4" :class="getAdIconClass(ad.type)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getAdIcon(ad.type)"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="px-1.5 py-0.5 text-[10px] font-bold rounded" :class="getAdBadgeClass(ad.type)">{{ getAdLabel(ad.type) }}</span>
                        <span class="text-[13px] font-medium text-slate-900 truncate">{{ ad.title }}</span>
                      </div>
                      <p class="text-[11px] text-slate-400 mt-0.5">~{{ formatShortDate(ad.expiresAt) }}</p>
                    </div>
                  </component>
                </div>

                <div v-else class="p-6 text-center">
                  <div class="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <svg class="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
                    </svg>
                  </div>
                  <p class="text-[12px] text-slate-400">진행 중인 광고 없음</p>
                </div>
              </div>

              <!-- 신청한 광고 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <p class="text-[14px] font-bold text-slate-900">신청한 광고</p>
                  <span class="text-[12px] font-medium text-amber-600">{{ pendingAds.length }}개 대기 중</span>
                </div>

                <div v-if="pendingAds.length > 0" class="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  <component
                    v-for="ad in pendingAds.slice(0, 4)"
                    :key="ad.type + '-pending-' + ad.id"
                    :is="'router-link'"
                    :to="getAdRoute(ad.type, 'my')"
                    class="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  >
                    <div class="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getAdIcon(ad.type)"/>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded">{{ getAdLabel(ad.type) }}</span>
                        <span class="text-[13px] font-medium text-slate-900 truncate">{{ ad.title }}</span>
                      </div>
                      <p class="text-[11px] text-slate-400 mt-0.5">{{ formatDate(ad.createdAt) }} 신청</p>
                    </div>
                    <span class="px-2 py-1 bg-amber-50 text-amber-600 text-[11px] font-bold rounded-lg">심사 중</span>
                  </component>
                </div>

                <div v-else class="p-6 text-center">
                  <div class="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                    <svg class="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </div>
                  <p class="text-[12px] text-slate-400">대기 중인 광고 없음</p>
                </div>
              </div>

              <!-- 파트너스 포인트 내역 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div class="flex items-center gap-2">
                    <div class="w-5 h-5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded flex items-center justify-center">
                      <span class="text-[8px] font-black text-white">PP</span>
                    </div>
                    <p class="text-[14px] font-bold text-slate-900">파트너스 포인트 내역</p>
                  </div>
                  <router-link to="/partners/point" class="text-[12px] font-medium text-blue-600">전체보기</router-link>
                </div>

                <div v-if="recentTransactions.length > 0" class="divide-y divide-slate-100">
                  <div
                    v-for="tx in recentTransactions.slice(0, 4)"
                    :key="tx.id"
                    class="flex items-center justify-between px-4 py-3"
                  >
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg flex items-center justify-center"
                           :class="tx.type === 'charge' ? 'bg-emerald-100' : tx.type === 'refund' ? 'bg-blue-100' : 'bg-rose-100'">
                        <svg v-if="tx.type === 'charge'" class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                        </svg>
                        <svg v-else-if="tx.type === 'refund'" class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                        </svg>
                        <svg v-else class="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                        </svg>
                      </div>
                      <div>
                        <p class="text-[13px] font-medium text-slate-900">{{ tx.description }}</p>
                        <p class="text-[11px] text-slate-400">{{ formatDate(tx.createdAt) }}</p>
                      </div>
                    </div>
                    <p class="text-[14px] font-bold"
                       :class="tx.type === 'use' ? 'text-rose-600' : 'text-emerald-600'">
                      {{ tx.type === 'use' ? '-' : '+' }}{{ formatNumber(Math.abs(tx.amount)) }}P
                    </p>
                  </div>
                </div>

                <div v-else class="p-8 text-center">
                  <p class="text-[13px] text-slate-400">거래 내역이 없습니다</p>
                </div>
              </div>
            </div>

            <!-- 오른쪽 사이드 섹션 -->
            <div class="w-full lg:w-80 space-y-4">
              <!-- 파트너 커뮤니티 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <router-link
                  to="/partners/community"
                  class="flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
                    </svg>
                    <p class="text-[14px] font-bold text-slate-900">파트너 커뮤니티</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>

                <div v-if="recentCommunityPosts.length > 0" class="divide-y divide-slate-100">
                  <router-link
                    v-for="post in recentCommunityPosts.slice(0, 3)"
                    :key="post.id"
                    to="/partners/community"
                    class="flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    <span class="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded">{{ getCommunityCategory(post.category) }}</span>
                    <span class="text-[13px] text-slate-700 truncate flex-1">{{ post.title }}</span>
                  </router-link>
                </div>
                <div v-else class="p-4 text-center">
                  <p class="text-[12px] text-slate-400">게시글이 없습니다</p>
                </div>
              </div>

              <!-- 공지사항 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <router-link
                  to="/partners/notice"
                  class="flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
                    </svg>
                    <p class="text-[14px] font-bold text-slate-900">공지사항</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>

                <div v-if="recentNotices.length > 0" class="divide-y divide-slate-100">
                  <router-link
                    v-for="notice in recentNotices.slice(0, 3)"
                    :key="notice.id"
                    to="/partners/notice"
                    class="flex items-start gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    <div class="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></div>
                    <div class="min-w-0 flex-1">
                      <p class="text-[13px] text-slate-700 truncate">{{ notice.title }}</p>
                      <p class="text-[11px] text-slate-400 mt-0.5">{{ formatDate(notice.createdAt) }}</p>
                    </div>
                  </router-link>
                </div>
                <div v-else class="p-4 text-center">
                  <p class="text-[12px] text-slate-400">공지사항이 없습니다</p>
                </div>
              </div>

              <!-- 업데이트 -->
              <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                    </svg>
                    <p class="text-[14px] font-bold text-slate-900">업데이트</p>
                  </div>
                </div>

                <div v-if="updates.length > 0" class="divide-y divide-slate-100">
                  <div
                    v-for="update in updates.slice(0, 3)"
                    :key="update.id"
                    class="px-4 py-2.5"
                  >
                    <div class="flex items-center gap-2 mb-1">
                      <span :class="['px-1.5 py-0.5 text-[10px] font-bold rounded', getUpdateTypeBadgeClass(update.type)]">
                        {{ getUpdateTypeLabel(update.type) }}
                      </span>
                      <span v-if="update.version" class="text-[10px] text-slate-400">{{ update.version }}</span>
                    </div>
                    <p class="text-[13px] text-slate-700">{{ update.title }}</p>
                  </div>
                </div>
                <div v-else class="p-4 text-center">
                  <p class="text-[12px] text-slate-400">업데이트 내역이 없습니다</p>
                </div>
              </div>

              <!-- 건의/신고 바로가기 -->
              <router-link
                to="/partners/feedback"
                class="block bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-300 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg class="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-[14px] font-bold text-slate-900">건의/신고</p>
                    <p class="text-[12px] text-slate-400">건의사항이나 신고를 접수하세요</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              </router-link>

              <!-- 정사각형 배너 슬라이더 -->
              <div v-if="squareBanners.length > 0" class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div class="relative aspect-square">
                  <a
                    v-if="squareBanners[currentSquareBannerIndex]"
                    :href="squareBanners[currentSquareBannerIndex].linkUrl || '#'"
                    @click="handleSquareBannerClick($event, squareBanners[currentSquareBannerIndex])"
                    class="block w-full h-full"
                  >
                    <img
                      :src="squareBanners[currentSquareBannerIndex].imageUrl"
                      class="w-full h-full object-cover"
                      alt="광고"
                    />
                  </a>
                  <!-- 배너 컨트롤 (1/2 왼쪽, <> 오른쪽) -->
                  <div class="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <span class="px-2 py-0.5 bg-black/40 text-white text-[10px] font-medium rounded-full">
                      {{ currentSquareBannerIndex + 1 }}/{{ squareBanners.length }}
                    </span>
                    <div class="flex items-center gap-1">
                      <button
                        @click="prevSquareBanner"
                        class="w-6 h-6 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                      </button>
                      <button
                        @click="nextSquareBanner"
                        class="w-6 h-6 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                      >
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import { usePoint } from '@/composables/partners/usePoint'
import { useJump } from '@/composables/partners/useJump'
import { useCampaign } from '@/composables/partners/useCampaign'
import { usePartnerDashboard } from '@/composables/partners/usePartnerDashboard'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'PartnersHomeView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const router = useRouter()
    const { user, userProfile, loading: authLoading } = useAuth()
    const { balance: pointBalance, transactions, loadBalance, loadTransactions } = usePoint()
    const { myJumps, loadMyJumps } = useJump()
    const { myCampaigns, loadMyCampaigns } = useCampaign()
    const {
      dashboardBanners,
      updates,
      squareBanners,
      loadAll: loadDashboardData,
      getUpdateTypeLabel,
      getUpdateTypeBadgeClass
    } = usePartnerDashboard()

    const loading = ref(true)
    const dataLoaded = ref(false)
    const currentProperty = ref(null)
    const pendingLeadsCount = ref(0)
    const myCustomersCount = ref(0)
    const openLeadsCount = ref(0)
    const recentNotices = ref([])
    const recentCommunityPosts = ref([])

    // 광고 상태
    const activeAds = ref([])
    const pendingAds = ref([])

    // 배너 인덱스
    const currentBannerIndex = ref(0)
    const currentSquareBannerIndex = ref(0)

    // 인사 메시지
    const greetingMessage = computed(() => {
      const hour = new Date().getHours()
      let greeting = '안녕하세요'
      if (hour >= 5 && hour < 12) greeting = '좋은 아침이에요'
      else if (hour >= 12 && hour < 18) greeting = '좋은 오후에요'
      else if (hour >= 18 && hour < 22) greeting = '좋은 저녁이에요'
      else greeting = '늦은 시간이네요'

      const name = userProfile.value?.name || '파트너'
      return `${greeting}, ${name}님!`
    })

    // 온보딩 스텝
    const onboardingSteps = computed(() => [
      {
        id: 1,
        label: '현장 소속 승인받기',
        completed: !!currentProperty.value,
        action: () => router.push('/partners/property/change')
      },
      {
        id: 2,
        label: '캠페인 만들기',
        completed: myCampaigns.value.length > 0,
        action: () => router.push('/partners/ad/create')
      },
      {
        id: 3,
        label: '첫 광고 등록하기',
        completed: activeAds.value.length > 0 || pendingAds.value.length > 0,
        action: () => router.push('/partners/adbanner/purchase')
      },
      {
        id: 4,
        label: '점프 사용해보기',
        completed: myJumps.value.length > 0,
        action: () => router.push('/partners/jump/purchase')
      }
    ])

    const onboardingProgress = computed(() => {
      const completedCount = onboardingSteps.value.filter(s => s.completed).length
      return Math.round((completedCount / onboardingSteps.value.length) * 100)
    })

    // 활성 캠페인
    const activeCampaigns = computed(() => {
      const now = new Date()
      return myCampaigns.value.filter(c => {
        if (c.status === 'expired') return false
        const endDate = new Date(c.endDate)
        return endDate >= now
      }).slice(0, 3)
    })

    // 활성 점프
    const activeJumps = computed(() => {
      const now = new Date()
      const today = now.toISOString().split('T')[0]
      return myJumps.value.filter(j => {
        if (j.status !== 'active') return false
        return j.startDate <= today && j.endDate >= today
      }).slice(0, 3)
    })

    // 최근 거래 내역
    const recentTransactions = computed(() => {
      return transactions.value.slice(0, 5)
    })

    // 배너 네비게이션
    const prevBanner = () => {
      if (dashboardBanners.value.length <= 1) return
      currentBannerIndex.value = currentBannerIndex.value === 0
        ? dashboardBanners.value.length - 1
        : currentBannerIndex.value - 1
    }

    const nextBanner = () => {
      if (dashboardBanners.value.length <= 1) return
      currentBannerIndex.value = currentBannerIndex.value === dashboardBanners.value.length - 1
        ? 0
        : currentBannerIndex.value + 1
    }

    const prevSquareBanner = () => {
      if (squareBanners.value.length <= 1) return
      currentSquareBannerIndex.value = currentSquareBannerIndex.value === 0
        ? squareBanners.value.length - 1
        : currentSquareBannerIndex.value - 1
    }

    const nextSquareBanner = () => {
      if (squareBanners.value.length <= 1) return
      currentSquareBannerIndex.value = currentSquareBannerIndex.value === squareBanners.value.length - 1
        ? 0
        : currentSquareBannerIndex.value + 1
    }

    const handleBannerClick = (e, banner) => {
      if (!banner.linkUrl) {
        e.preventDefault()
        return
      }
      if (banner.linkUrl.startsWith('/')) {
        e.preventDefault()
        router.push(banner.linkUrl)
      }
    }

    const handleSquareBannerClick = (e, banner) => {
      if (!banner.linkUrl) {
        e.preventDefault()
        return
      }
      if (banner.linkUrl.startsWith('/')) {
        e.preventDefault()
        router.push(banner.linkUrl)
      }
    }

    // 현재 소속 현장 조회
    const fetchCurrentProperty = async () => {
      if (!user.value) return

      try {
        let q = query(
          collection(db, 'propertyPartners'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'active')
        )
        let snapshot = await getDocs(q)

        if (!snapshot.empty) {
          currentProperty.value = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          }
          return
        }

        q = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'approved')
        )
        snapshot = await getDocs(q)

        if (!snapshot.empty) {
          currentProperty.value = {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
          }
        }
      } catch (error) {
        console.error('현장 조회 실패:', error)
      }
    }

    // 신규 지목 DB 수 조회
    const fetchPendingLeads = async () => {
      if (!user.value) return
      try {
        const q = query(
          collection(db, 'consultationRequests'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'pending')
        )
        const snapshot = await getDocs(q)
        pendingLeadsCount.value = snapshot.size
      } catch (error) {
        console.error('신규 DB 조회 실패:', error)
      }
    }

    // 내 고객 수 조회
    const fetchMyCustomers = async () => {
      if (!user.value) return
      try {
        const q1 = query(
          collection(db, 'consultationRequests'),
          where('partnerId', '==', user.value.uid),
          where('status', '==', 'contacted')
        )
        const q2 = query(
          collection(db, 'consultationRequests'),
          where('pickedBy', '==', user.value.uid)
        )

        const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)])
        const ids = new Set()
        snapshot1.docs.forEach(doc => ids.add(doc.id))
        snapshot2.docs.forEach(doc => ids.add(doc.id))
        myCustomersCount.value = ids.size
      } catch (error) {
        console.error('고객 수 조회 실패:', error)
      }
    }

    // 선착순 줍기 가능한 DB 수 조회
    const fetchOpenLeads = async () => {
      if (!user.value || !currentProperty.value) return
      try {
        const q = query(
          collection(db, 'consultationRequests'),
          where('propertyId', '==', currentProperty.value.propertyId),
          where('status', '==', 'expired')
        )
        const snapshot = await getDocs(q)
        openLeadsCount.value = snapshot.size
      } catch (error) {
        console.error('유실 DB 조회 실패:', error)
      }
    }

    // 최근 공지사항 조회
    const fetchRecentNotices = async () => {
      try {
        const q = query(
          collection(db, 'partnerNotices'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          limit(3)
        )
        const snapshot = await getDocs(q)
        recentNotices.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('공지사항 조회 실패:', error)
      }
    }

    // 최근 커뮤니티 게시글 조회
    const fetchRecentCommunityPosts = async () => {
      try {
        const q = query(
          collection(db, 'partnerCommunity'),
          orderBy('createdAt', 'desc'),
          limit(3)
        )
        const snapshot = await getDocs(q)
        recentCommunityPosts.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('커뮤니티 게시글 조회 실패:', error)
      }
    }

    // 커뮤니티 카테고리 라벨
    const getCommunityCategory = (categoryId) => {
      const categories = {
        free: '자유',
        info: '정보',
        qna: 'Q&A',
        deal: '거래'
      }
      return categories[categoryId] || categoryId
    }

    // 광고 상태 조회
    const fetchAdStatus = async () => {
      if (!user.value) return

      const active = []
      const pending = []
      const now = new Date().toISOString()

      try {
        // 인앱 광고
        const adBannerQ = query(
          collection(db, 'adBanners'),
          where('partnerId', '==', user.value.uid)
        )
        const adBannerSnapshot = await getDocs(adBannerQ)
        adBannerSnapshot.docs.forEach(doc => {
          const data = { id: doc.id, type: 'adbanner', ...doc.data() }
          if (data.status === 'approved' && data.expiresAt > now) {
            active.push(data)
          } else if (data.status === 'pending') {
            pending.push(data)
          }
        })

        // 팝업 광고
        const popupQ = query(
          collection(db, 'popupAds'),
          where('partnerId', '==', user.value.uid)
        )
        const popupSnapshot = await getDocs(popupQ)
        popupSnapshot.docs.forEach(doc => {
          const data = { id: doc.id, type: 'popup', ...doc.data() }
          if (data.status === 'approved' && data.expiresAt > now) {
            active.push(data)
          } else if (data.status === 'pending') {
            pending.push(data)
          }
        })

        // 스플래시 광고
        const splashQ = query(
          collection(db, 'splashAds'),
          where('partnerId', '==', user.value.uid)
        )
        const splashSnapshot = await getDocs(splashQ)
        splashSnapshot.docs.forEach(doc => {
          const data = { id: doc.id, type: 'splash', ...doc.data() }
          if (data.status === 'approved' && data.expiresAt > now) {
            active.push(data)
          } else if (data.status === 'pending') {
            pending.push(data)
          }
        })

        activeAds.value = active
        pendingAds.value = pending.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      } catch (error) {
        console.error('광고 상태 조회 실패:', error)
      }
    }

    // 광고 타입별 헬퍼 함수
    const getAdLabel = (type) => {
      const labels = { adbanner: '인앱', popup: '팝업', splash: '스플래시' }
      return labels[type] || type
    }

    const getAdBgClass = (type) => {
      const classes = { adbanner: 'bg-rose-100', popup: 'bg-cyan-100', splash: 'bg-pink-100' }
      return classes[type] || 'bg-slate-100'
    }

    const getAdIconClass = (type) => {
      const classes = { adbanner: 'text-rose-600', popup: 'text-cyan-600', splash: 'text-pink-600' }
      return classes[type] || 'text-slate-600'
    }

    const getAdBadgeClass = (type) => {
      const classes = {
        adbanner: 'bg-rose-100 text-rose-700',
        popup: 'bg-cyan-100 text-cyan-700',
        splash: 'bg-pink-100 text-pink-700'
      }
      return classes[type] || 'bg-slate-100 text-slate-700'
    }

    const getAdIcon = (type) => {
      const icons = {
        adbanner: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
        popup: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
        splash: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
      }
      return icons[type] || ''
    }

    const getAdRoute = (type, page) => {
      const routes = {
        adbanner: `/partners/adbanner/${page}`,
        popup: `/partners/popup/${page}`,
        splash: `/partners/splash/${page}`
      }
      return routes[type] || '/partners'
    }

    const getRemainingDays = (endDate) => {
      const end = new Date(endDate)
      const now = new Date()
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
      return Math.max(0, diff)
    }

    const formatShortDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      if (diff < 60 * 60 * 1000) return `${Math.floor(diff / (60 * 1000))}분 전`
      if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}시간 전`
      if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / (24 * 60 * 60 * 1000))}일 전`
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    const formatNumber = (num) => {
      if (!num) return '0'
      return num.toLocaleString()
    }

    const formatCompact = (num) => {
      if (!num) return '0'
      if (num >= 10000) return (num / 10000).toFixed(1).replace(/\.0$/, '') + '만'
      if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
      return num.toString()
    }

    // 데이터 로드 함수
    const loadAllData = async () => {
      if (!user.value || dataLoaded.value) return

      loading.value = true
      try {
        // 대시보드 배너/업데이트 먼저 로드
        await loadDashboardData()

        // 1단계: 현장 정보 먼저 조회
        await fetchCurrentProperty()

        // 2단계: 나머지 데이터 병렬 조회
        await Promise.all([
          loadBalance(),
          loadTransactions(10),
          loadMyCampaigns(),
          loadMyJumps(),
          fetchPendingLeads(),
          fetchMyCustomers(),
          fetchRecentNotices(),
          fetchRecentCommunityPosts(),
          fetchAdStatus()
        ])

        // 3단계: 현장 조회 후 선착순 줍기 가능 DB 조회
        if (currentProperty.value) {
          await fetchOpenLeads()
        }

        dataLoaded.value = true
      } catch (error) {
        console.error('대시보드 데이터 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    // auth 로딩 완료 후 데이터 로드
    watch(authLoading, (isLoading) => {
      if (!isLoading && user.value) {
        loadAllData()
      }
    }, { immediate: true })

    // user 변경 감지
    watch(user, (newUser) => {
      if (newUser && !dataLoaded.value) {
        loadAllData()
      }
    })

    onMounted(() => {
      if (!authLoading.value && user.value) {
        loadAllData()
      }
    })

    return {
      userProfile,
      loading,
      greetingMessage,
      currentProperty,
      pointBalance,
      pendingLeadsCount,
      myCustomersCount,
      openLeadsCount,
      activeCampaigns,
      activeJumps,
      activeAds,
      pendingAds,
      recentTransactions,
      recentNotices,
      recentCommunityPosts,
      dashboardBanners,
      updates,
      squareBanners,
      currentBannerIndex,
      currentSquareBannerIndex,
      onboardingSteps,
      onboardingProgress,
      prevBanner,
      nextBanner,
      prevSquareBanner,
      nextSquareBanner,
      handleBannerClick,
      handleSquareBannerClick,
      getRemainingDays,
      formatShortDate,
      formatDate,
      formatNumber,
      formatCompact,
      getAdLabel,
      getAdBgClass,
      getAdIconClass,
      getAdBadgeClass,
      getAdIcon,
      getAdRoute,
      getCommunityCategory,
      getUpdateTypeLabel,
      getUpdateTypeBadgeClass
    }
  }
}
</script>
